import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Play, RotateCcw, CheckCircle, Target, Zap, Trophy } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

interface Sprite {
    id: number;
    x: number;
    y: number;
    width: number;
    height: number;
    vx: number;
    vy: number;
    emoji: string;
    type: 'player' | 'coin' | 'enemy';
}

export const SpriteCollision = () => {
    const { completeModule } = useAcademyStore();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(3);
    const [gameOver, setGameOver] = useState(false);
    const [showHitboxes, setShowHitboxes] = useState(true);
    const [completed, setCompleted] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const animationRef = useRef<number>();
    const keysRef = useRef<Set<string>>(new Set());

    const [player, setPlayer] = useState<Sprite>({
        id: 0, x: 200, y: 250, width: 40, height: 40, vx: 0, vy: 0, emoji: '🦍', type: 'player'
    });

    const [coins, setCoins] = useState<Sprite[]>([
        { id: 1, x: 100, y: 100, width: 30, height: 30, vx: 0, vy: 0, emoji: '🍌', type: 'coin' },
        { id: 2, x: 300, y: 150, width: 30, height: 30, vx: 0, vy: 0, emoji: '🍌', type: 'coin' },
        { id: 3, x: 150, y: 200, width: 30, height: 30, vx: 0, vy: 0, emoji: '🍌', type: 'coin' },
    ]);

    const [enemies, setEnemies] = useState<Sprite[]>([
        { id: 10, x: 50, y: 50, width: 35, height: 35, vx: 2, vy: 1, emoji: '🐍', type: 'enemy' },
    ]);

    // Collision detection: AABB (Axis-Aligned Bounding Box)
    const checkCollision = (a: Sprite, b: Sprite): boolean => {
        return (
            a.x < b.x + b.width &&
            a.x + a.width > b.x &&
            a.y < b.y + b.height &&
            a.y + a.height > b.y
        );
    };

    // Key handlers
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => keysRef.current.add(e.key);
        const handleKeyUp = (e: KeyboardEvent) => keysRef.current.delete(e.key);
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    // Game loop
    useEffect(() => {
        if (!isRunning || gameOver || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d')!;

        const gameLoop = () => {
            // Clear
            ctx.fillStyle = '#e8f5e9';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Move player based on keys
            const newPlayer = { ...player };
            const speed = 5;
            if (keysRef.current.has('ArrowUp') || keysRef.current.has('w')) newPlayer.y -= speed;
            if (keysRef.current.has('ArrowDown') || keysRef.current.has('s')) newPlayer.y += speed;
            if (keysRef.current.has('ArrowLeft') || keysRef.current.has('a')) newPlayer.x -= speed;
            if (keysRef.current.has('ArrowRight') || keysRef.current.has('d')) newPlayer.x += speed;

            // Keep player in bounds
            newPlayer.x = Math.max(0, Math.min(canvas.width - newPlayer.width, newPlayer.x));
            newPlayer.y = Math.max(0, Math.min(canvas.height - newPlayer.height, newPlayer.y));
            setPlayer(newPlayer);

            // Move enemies
            setEnemies(prev => prev.map(enemy => {
                const newX = enemy.x + enemy.vx;
                const newY = enemy.y + enemy.vy;
                let newVx = enemy.vx;
                let newVy = enemy.vy;

                if (newX < 0 || newX > canvas.width - enemy.width) newVx *= -1;
                if (newY < 0 || newY > canvas.height - enemy.height) newVy *= -1;

                return { ...enemy, x: newX, y: newY, vx: newVx, vy: newVy };
            }));

            // Check coin collisions
            setCoins(prev => {
                const remaining = prev.filter(coin => !checkCollision(newPlayer, coin));
                if (remaining.length < prev.length) {
                    setScore(s => s + 10);
                }
                return remaining;
            });

            // Check enemy collisions
            enemies.forEach(enemy => {
                if (checkCollision(newPlayer, enemy)) {
                    setLives(l => {
                        const newLives = l - 1;
                        if (newLives <= 0) {
                            setGameOver(true);
                            setIsRunning(false);
                        }
                        return Math.max(0, newLives);
                    });
                    // Push player back
                    setPlayer(p => ({ ...p, x: 200, y: 250 }));
                }
            });

            // Draw hitboxes if enabled
            if (showHitboxes) {
                ctx.strokeStyle = '#22c55e';
                ctx.lineWidth = 2;
                ctx.strokeRect(newPlayer.x, newPlayer.y, newPlayer.width, newPlayer.height);

                ctx.strokeStyle = '#eab308';
                coins.forEach(c => ctx.strokeRect(c.x, c.y, c.width, c.height));

                ctx.strokeStyle = '#ef4444';
                enemies.forEach(e => ctx.strokeRect(e.x, e.y, e.width, e.height));
            }

            // Draw sprites
            ctx.font = '30px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            coins.forEach(c => ctx.fillText(c.emoji, c.x + c.width / 2, c.y + c.height / 2));
            enemies.forEach(e => ctx.fillText(e.emoji, e.x + e.width / 2, e.y + e.height / 2));
            ctx.font = '35px Arial';
            ctx.fillText(newPlayer.emoji, newPlayer.x + newPlayer.width / 2, newPlayer.y + newPlayer.height / 2);

            // Check win
            if (coins.length === 0 && !completed) {
                setCompleted(true);
                completeModule('sprite-collision', 100);
                setShowSuccess(true);
            }

            animationRef.current = requestAnimationFrame(gameLoop);
        };

        animationRef.current = requestAnimationFrame(gameLoop);
        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, [isRunning, gameOver, player, coins, enemies, showHitboxes, completed, completeModule]);

    const startGame = () => {
        setIsRunning(true);
        setGameOver(false);
        setScore(0);
        setLives(3);
        setPlayer({ id: 0, x: 200, y: 250, width: 40, height: 40, vx: 0, vy: 0, emoji: '🦍', type: 'player' });
        setCoins([
            { id: 1, x: 100, y: 100, width: 30, height: 30, vx: 0, vy: 0, emoji: '🍌', type: 'coin' },
            { id: 2, x: 300, y: 150, width: 30, height: 30, vx: 0, vy: 0, emoji: '🍌', type: 'coin' },
            { id: 3, x: 150, y: 200, width: 30, height: 30, vx: 0, vy: 0, emoji: '🍌', type: 'coin' },
        ]);
        setEnemies([
            { id: 10, x: 50, y: 50, width: 35, height: 35, vx: 2, vy: 1, emoji: '🐍', type: 'enemy' },
        ]);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-100 to-emerald-50 font-sans pt-24">
            <div className="max-w-5xl mx-auto p-8">
                <div className="text-center mb-6">
                    <h1 className="text-4xl font-black font-comic text-slate-800 mb-2">
                        💥 Bump & Bounce
                    </h1>
                    <div className="bg-white/80 backdrop-blur-sm p-4 rounded-2xl inline-block shadow-sm border border-green-100 max-w-2xl">
                        <h3 className="text-lg font-bold text-green-700 mb-1">🎯 Mission Brief</h3>
                        <p className="text-slate-600">
                            The forest is guarded by snakes! Use your arrow keys to guide the gorilla.
                            <br />
                            <strong>Goal:</strong> Collect all <span className="font-bold text-yellow-600">3 Bananas</span> without touching a snake!
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Game Canvas */}
                    <div className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-xl">
                        {/* HUD */}
                        <div className="flex justify-between mb-4">
                            <div className="flex gap-4">
                                <div className="bg-yellow-100 px-4 py-2 rounded-xl">
                                    <span className="font-bold text-yellow-700">🍌 Score: {score}</span>
                                </div>
                                <div className="bg-red-100 px-4 py-2 rounded-xl">
                                    <span className="font-bold text-red-700">❤️ Lives: {lives}</span>
                                </div>
                            </div>
                            <div className="bg-slate-100 px-4 py-2 rounded-xl text-sm">
                                🍌 Left: {coins.length}
                            </div>
                        </div>

                        <canvas
                            ref={canvasRef}
                            width={450}
                            height={300}
                            className="w-full rounded-2xl border-4 border-green-200"
                            tabIndex={0}
                        />

                        <div className="flex justify-center gap-4 mt-6">
                            {!isRunning ? (
                                <button
                                    onClick={startGame}
                                    className="bg-green-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-green-600 flex items-center gap-2"
                                >
                                    <Play size={18} /> {gameOver ? 'Try Again' : 'Start Game'}
                                </button>
                            ) : (
                                <div className="text-slate-500">Use Arrow Keys or WASD to move!</div>
                            )}
                        </div>

                        {gameOver && (
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="mt-4 text-center bg-red-50 p-4 rounded-xl border-2 border-red-200"
                            >
                                <div className="text-2xl font-black text-red-600">Game Over! 😢</div>
                                <div className="text-slate-600">You scored {score} points</div>
                            </motion.div>
                        )}

                        {completed && coins.length === 0 && (
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="mt-4 text-center bg-green-50 p-4 rounded-xl border-2 border-green-200"
                            >
                                <div className="text-2xl font-black text-green-600">You Win! 🎉</div>
                                <div className="text-slate-600">All bananas collected!</div>
                            </motion.div>
                        )}
                    </div>

                    {/* Explanation */}
                    <div className="space-y-6">
                        <div className="bg-white rounded-3xl p-6 shadow-xl">
                            <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                                <Target className="text-green-500" /> How It Works
                            </h3>

                            <div className="bg-slate-50 rounded-xl p-4 font-mono text-xs mb-4">
                                <div className="text-slate-500">// Check if boxes overlap</div>
                                <div className="text-blue-600">if (gorilla.x &lt; coin.x + coin.width</div>
                                <div className="text-blue-600">&nbsp;&nbsp;&amp;&amp; gorilla.x + gorilla.width &gt; coin.x</div>
                                <div className="text-blue-600">&nbsp;&nbsp;&amp;&amp; gorilla.y &lt; coin.y + coin.height</div>
                                <div className="text-blue-600">&nbsp;&nbsp;&amp;&amp; gorilla.y + gorilla.height &gt; coin.y)</div>
                                <div className="text-green-600">{'{'} // COLLISION! {'}'}</div>
                            </div>

                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={showHitboxes}
                                    onChange={e => setShowHitboxes(e.target.checked)}
                                    className="accent-green-500"
                                />
                                <span className="text-sm">Show Hitboxes</span>
                            </label>
                        </div>

                        <div className="bg-green-50 rounded-2xl p-6 border-2 border-green-200">
                            <h3 className="font-bold text-green-800 mb-2">🎓 What You Learned</h3>
                            <ul className="text-green-700 text-sm space-y-1">
                                <li>• <strong>Hitbox:</strong> An invisible box around sprites</li>
                                <li>• <strong>AABB:</strong> Axis-Aligned Bounding Box</li>
                                <li>• Check if boxes <em>overlap</em> to detect collision</li>
                                <li>• Different collision = different reaction!</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            {showSuccess && (
                <ModuleSuccess
                    title="Collision Detection"
                    onRetry={() => {
                        setShowSuccess(false);
                        startGame();
                    }}
                >
                    <div className="bg-green-50 rounded-2xl p-4 text-left mx-auto max-w-sm">
                        <div className="font-bold text-green-800 mb-2">💥 Physics Engine:</div>
                        <ul className="text-green-700 text-sm space-y-1">
                            <li>• You wrote logic to check overlaps</li>
                            <li>• If <strong>Rect A</strong> touches <strong>Rect B</strong>...</li>
                            <li>• ...Trigger Event (Collect or Die)!</li>
                        </ul>
                    </div>
                </ModuleSuccess>
            )}

            {/* Educational Content Section */}
            <div className="max-w-5xl mx-auto px-8 pb-12 mt-12 space-y-8">

                {/* Theory Section */}
                <div className="bg-white p-8 rounded-3xl shadow-lg border-2 border-slate-200">
                    <h2 className="text-2xl font-black text-slate-800 mb-6 flex items-center gap-3">
                        📚 Understanding Collision Detection
                    </h2>

                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h3 className="font-bold text-lg text-slate-700 mb-3">How Do Games Know When Things Touch?</h3>
                            <p className="text-slate-600 mb-4">
                                In real life, things bump into each other. In games, we have to use MATH to check if pixels overlap!
                            </p>
                            <p className="text-slate-600 mb-4">
                                Checking every single pixel is too slow. Instead, we put invisible boxes around everything.
                                These are called <strong>Hitboxes</strong> (or Bounding Boxes).
                            </p>

                            <div className="bg-green-50 p-4 rounded-xl border border-green-200 mt-4">
                                <h4 className="font-bold text-green-800 mb-2">📦 AABB Collision</h4>
                                <p className="text-green-700 text-sm mb-3">
                                    <strong>"Axis-Aligned Bounding Box"</strong> - The simplest way to check collision.
                                    It just asks 4 questions:
                                </p>
                                <ul className="text-green-700 text-xs font-mono space-y-1">
                                    <li>1. Is <span className="text-red-500">Right Side A</span> &gt; <span className="text-blue-500">Left Side B</span>?</li>
                                    <li>2. Is <span className="text-red-500">Left Side A</span> &lt; <span className="text-blue-500">Right Side B</span>?</li>
                                    <li>3. Is <span className="text-red-500">Bottom A</span> &gt; <span className="text-blue-500">Top B</span>?</li>
                                    <li>4. Is <span className="text-red-500">Top A</span> &lt; <span className="text-blue-500">Bottom B</span>?</li>
                                </ul>
                                <p className="text-green-800 font-bold text-sm mt-3">If ALL happen, it's a HIT! 💥</p>
                            </div>
                        </div>

                        <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                            <h3 className="font-bold text-lg text-slate-700 mb-3">🎯 Types of Colliders</h3>

                            <div className="space-y-4">
                                <div className="flex gap-3">
                                    <div className="w-12 h-12 bg-white border-2 border-slate-400 rounded-lg flex items-center justify-center shrink-0">
                                        <div className="w-8 h-8 bg-blue-500"></div>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-700 text-sm">Box Collider</h4>
                                        <p className="text-slate-500 text-xs">Fastest math. Great for platforms, walls, and blocky games like Minecraft.</p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <div className="w-12 h-12 bg-white border-2 border-slate-400 rounded-lg flex items-center justify-center shrink-0">
                                        <div className="w-8 h-8 rounded-full bg-red-500"></div>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-700 text-sm">Circle Collider</h4>
                                        <p className="text-slate-500 text-xs">Great for balls and round characters. Uses distance formula (Pythagoras!).</p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <div className="w-12 h-12 bg-white border-2 border-slate-400 rounded-lg flex items-center justify-center shrink-0">
                                        <div className="w-0 h-0 border-l-[10px] border-l-transparent border-t-[15px] border-t-purple-500 border-r-[10px] border-r-transparent"></div>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-700 text-sm">Polygon/Mesh Collider</h4>
                                        <p className="text-slate-500 text-xs">Matches the exact shape. Very precise but computationally expensive (Slow!).</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Game Logic */}
                <div className="bg-slate-900 text-white p-8 rounded-3xl border border-slate-700">
                    <h2 className="text-xl font-bold text-white mb-4">🎮 Dealing with Impact</h2>
                    <p className="text-slate-400 mb-6">Detecting the hit is only Step 1. Step 2 is: <strong>What happens next?</strong></p>

                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="bg-slate-800 p-5 rounded-xl border border-slate-700">
                            <h3 className="font-bold text-blue-400 mb-2">Physics Response</h3>
                            <p className="text-slate-400 text-sm">
                                Bounce off! Transfer momentum.
                                (Example: Rocket League car hitting ball)
                            </p>
                        </div>
                        <div className="bg-slate-800 p-5 rounded-xl border border-slate-700">
                            <h3 className="font-bold text-green-400 mb-2">Trigger Event</h3>
                            <p className="text-slate-400 text-sm">
                                Collect item, open door, finish level.
                                (Example: Mario getting a coin)
                            </p>
                        </div>
                        <div className="bg-slate-800 p-5 rounded-xl border border-slate-700">
                            <h3 className="font-bold text-red-400 mb-2">Damage / Destroy</h3>
                            <p className="text-slate-400 text-sm">
                                Take damage, die, or destroy object.
                                (Example: Bullet hitting enemy)
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SpriteCollision;
